import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import CategoryRoundedIcon from '@mui/icons-material/CategoryRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import InsertChartOutlinedRoundedIcon from '@mui/icons-material/InsertChartOutlinedRounded';
import ScaleOutlinedIcon from '@mui/icons-material/ScaleOutlined';
import SummarizeRoundedIcon from '@mui/icons-material/SummarizeRounded';
import WarehouseOutlinedIcon from '@mui/icons-material/WarehouseOutlined';
import { AnimatedPage, AnimatedSection } from '../components/animations/MotionContainers';
import { VariedadNavAutocomplete } from '../components/entradas/VariedadNavAutocomplete';
import { DailyBalanceReportAction } from '../components/informes/DailyBalanceReportAction';
import { useFlujosCatalogs } from '../hooks/useFlujosCatalogs';
import { useFlujosEntradas } from '../hooks/useFlujosEntradas';
import { useFlujosVariedadesNav } from '../hooks/useFlujosVariedadesNav';
import {
  formatDisplayDate,
  getCampaignOptionsFromEntries,
  getCurrentCampaignRange,
} from '../utils/dashboardData';

const reportTypes = [
  {
    id: 'balance-variedad',
    title: 'Balance diario por variedad',
    description: 'Detalle diario y acumulado de una variedad dentro del periodo seleccionado.',
    icon: <CategoryRoundedIcon />,
  },
  {
    id: 'balance-global',
    title: 'Balance diario global',
    description: 'Resumen diario y acumulado de todas las variedades procesadas.',
    icon: <AssessmentOutlinedIcon />,
  },
  {
    id: 'balance-masas',
    title: 'Balance de masas',
    description: 'Balance acumulado de una variedad en el periodo y almacenes seleccionados.',
    icon: <ScaleOutlinedIcon />,
  },
  {
    id: 'mermas-totales',
    title: 'Grafico de mermas totales',
    description: 'Evolucion de industria, podrido y merma total.',
    icon: <InsertChartOutlinedRoundedIcon />,
  },
];

const ReportTypeCard = ({ report, selected, onSelect }) => (
  <Card
    sx={{
      height: '100%',
      borderColor: selected ? 'primary.main' : 'divider',
      boxShadow: selected ? '0 8px 22px rgba(255,121,0,0.13)' : undefined,
    }}
  >
    <CardActionArea
      onClick={onSelect}
      aria-pressed={selected}
      sx={{ height: '100%', p: 2.25, alignItems: 'flex-start' }}
    >
      <Stack direction="row" spacing={1.5} sx={{ alignItems: 'flex-start' }}>
        <Box
          sx={{
            width: 42,
            height: 42,
            borderRadius: 1.5,
            bgcolor: selected ? 'primary.main' : 'rgba(255,121,0,0.09)',
            color: selected ? 'primary.contrastText' : 'primary.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flex: '0 0 auto',
          }}
        >
          {report.icon}
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Stack direction="row" spacing={1} sx={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Typography variant="h3" sx={{ fontSize: 17 }}>
              {report.title}
            </Typography>
            {selected && <CheckCircleRoundedIcon color="primary" fontSize="small" />}
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
            {report.description}
          </Typography>
        </Box>
      </Stack>
    </CardActionArea>
  </Card>
);

export const Informes = () => {
  const {
    almacenes,
    variedades: variedadesFlujos,
    cajasPalet,
    pesoPalotMermaDefault,
    loading: catalogsLoading,
    error: catalogsError,
  } = useFlujosCatalogs();
  const {
    rows: variedades,
    loading: variedadesLoading,
    error: variedadesError,
  } = useFlujosVariedadesNav();
  const { rows: entradas, loading: entradasLoading, error: entradasError } = useFlujosEntradas({
    limit: 500,
    sortBy: 'fecha',
    sortDirection: 'desc',
  }, { almacenes, variedades: variedadesFlujos, cajasPalet });
  const currentCampaign = useMemo(() => getCurrentCampaignRange(), []);
  const campaignOptions = useMemo(
    () => getCampaignOptionsFromEntries(entradas),
    [entradas]
  );
  const [selectedReportId, setSelectedReportId] = useState(reportTypes[0].id);
  const [selectedCampaignStartYear, setSelectedCampaignStartYear] = useState(currentCampaign.startYear);
  const [dateRange, setDateRange] = useState({
    desde: currentCampaign.startValue,
    hasta: currentCampaign.endValue,
  });
  const [selectedWarehouseIds, setSelectedWarehouseIds] = useState([]);
  const [selectedVarietyId, setSelectedVarietyId] = useState('');

  const selectedCampaign = useMemo(
    () => campaignOptions.find((campaign) => campaign.startYear === selectedCampaignStartYear) || currentCampaign,
    [campaignOptions, currentCampaign, selectedCampaignStartYear]
  );
  const selectedReport = reportTypes.find((report) => report.id === selectedReportId) || reportTypes[0];
  const requiresVariety = selectedReportId === 'balance-variedad' || selectedReportId === 'balance-masas';
  const supportsOptionalVariety = selectedReportId === 'mermas-totales';
  const allWarehousesSelected = selectedWarehouseIds.length === 0;

  useEffect(() => {
    if (requiresVariety && !selectedVarietyId && variedades.length > 0) {
      setSelectedVarietyId(variedades[0].id);
    }
  }, [requiresVariety, selectedVarietyId, variedades]);

  const changeCampaign = (startYear) => {
    const campaign = campaignOptions.find((option) => option.startYear === startYear) || currentCampaign;

    setSelectedCampaignStartYear(startYear);
    setDateRange({ desde: campaign.startValue, hasta: campaign.endValue });
  };

  const toggleWarehouse = (warehouseId) => {
    setSelectedWarehouseIds((current) => {
      if (current.includes(warehouseId)) {
        return current.filter((id) => id !== warehouseId);
      }

      const nextIds = [...current, warehouseId];
      return nextIds.length === almacenes.length ? [] : nextIds;
    });
  };

  const selectReport = (reportId) => {
    setSelectedReportId(reportId);

    if (reportId !== 'balance-variedad' && reportId !== 'balance-masas') {
      setSelectedVarietyId('');
    }
  };

  return (
    <AnimatedPage>
      <Stack spacing={2.5}>
        <AnimatedSection>
          <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', minWidth: 0 }}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: '50%',
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flex: '0 0 auto',
              }}
            >
              <SummarizeRoundedIcon />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h1">Informes</Typography>
              <Typography variant="body2" color="text.secondary">
                Balances y evolucion de los flujos de fruta
              </Typography>
            </Box>
          </Stack>
        </AnimatedSection>

        {(catalogsLoading || variedadesLoading || entradasLoading || catalogsError || variedadesError || entradasError) && (
          <Alert
            severity={catalogsError || variedadesError || entradasError ? 'error' : 'info'}
            variant="outlined"
          >
            {catalogsError || variedadesError || entradasError || 'Cargando datos...'}
          </Alert>
        )}

        <AnimatedSection>
          <Box>
            <Typography variant="h3" sx={{ mb: 1.5 }}>
              Tipo de informe
            </Typography>
            <Grid container spacing={2}>
              {reportTypes.map((report) => (
                <Grid key={report.id} size={{ xs: 12, sm: 6, lg: 3 }}>
                  <ReportTypeCard
                    report={report}
                    selected={selectedReportId === report.id}
                    onSelect={() => selectReport(report.id)}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        </AnimatedSection>

        <AnimatedSection>
          <Box
            sx={{
              bgcolor: 'background.paper',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              overflow: 'hidden',
            }}
          >
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              sx={{ px: 2.25, py: 2, alignItems: { xs: 'flex-start', sm: 'center' }, borderBottom: '1px solid', borderColor: 'divider' }}
            >
              <CalendarMonthOutlinedIcon color="primary" />
              <Box sx={{ flex: 1 }}>
                <Typography variant="h3">Configurar {selectedReport.title.toLowerCase()}</Typography>
                <Typography variant="body2" color="text.secondary">
                  Selecciona el periodo y el origen de los datos
                </Typography>
              </Box>
            </Stack>

            <Box sx={{ p: 2.25 }}>
              <Grid container spacing={2}>
                {selectedReportId !== 'balance-masas' && (
                  <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
                    <TextField
                      select
                      fullWidth
                      label="Campaña"
                      value={selectedCampaignStartYear}
                      onChange={(event) => changeCampaign(Number(event.target.value))}
                      helperText={`${formatDisplayDate(selectedCampaign.startValue)} - ${formatDisplayDate(selectedCampaign.endValue)}`}
                    >
                      {campaignOptions.map((campaign) => (
                        <MenuItem key={campaign.startYear} value={campaign.startYear}>
                          {campaign.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                )}
                <Grid size={{ xs: 12, sm: 6, lg: selectedReportId === 'balance-masas' ? 4 : 3 }}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Desde"
                    value={dateRange.desde}
                    onChange={(event) => setDateRange((current) => ({ ...current, desde: event.target.value }))}
                    slotProps={{ inputLabel: { shrink: true } }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, lg: selectedReportId === 'balance-masas' ? 4 : 3 }}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Hasta"
                    value={dateRange.hasta}
                    onChange={(event) => setDateRange((current) => ({ ...current, hasta: event.target.value }))}
                    slotProps={{ inputLabel: { shrink: true } }}
                  />
                </Grid>
                {(requiresVariety || supportsOptionalVariety) && (
                  <Grid size={{ xs: 12, sm: 6, lg: selectedReportId === 'balance-masas' ? 4 : 3 }}>
                    <VariedadNavAutocomplete
                      options={variedades}
                      value={selectedVarietyId}
                      onChange={setSelectedVarietyId}
                      loading={variedadesLoading}
                      placeholder={supportsOptionalVariety ? 'Todas las variedades' : 'Seleccione variedad'}
                      required={requiresVariety}
                    />
                  </Grid>
                )}
              </Grid>

              <Box sx={{ mt: 2.25 }}>
                <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center', mb: 1.25 }}>
                  <WarehouseOutlinedIcon color="primary" fontSize="small" />
                  <Typography sx={{ fontWeight: 800 }}>Almacenes</Typography>
                </Stack>
                <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                  <Button
                    variant={allWarehousesSelected ? 'contained' : 'outlined'}
                    color={allWarehousesSelected ? 'primary' : 'secondary'}
                    size="small"
                    onClick={() => setSelectedWarehouseIds([])}
                  >
                    Todos
                  </Button>
                  {almacenes.map((almacen) => {
                    const active = selectedWarehouseIds.includes(almacen.id);

                    return (
                      <Button
                        key={almacen.id}
                        variant={active ? 'contained' : 'outlined'}
                        color={active ? 'primary' : 'secondary'}
                        size="small"
                        onClick={() => toggleWarehouse(almacen.id)}
                      >
                        {almacen.nombre}
                      </Button>
                    );
                  })}
                </Stack>
              </Box>

              {(requiresVariety || selectedReportId === 'balance-global' || supportsOptionalVariety) && (
                <DailyBalanceReportAction
                  almacenes={almacenes}
                  cajasPalet={cajasPalet}
                  dateRange={dateRange}
                  disabled={catalogsLoading || variedadesLoading || Boolean(catalogsError || variedadesError)}
                  mode={selectedReportId === 'balance-variedad' ? 'variety'
                    : selectedReportId === 'balance-masas' ? 'mass'
                      : supportsOptionalVariety ? 'losses' : 'global'}
                  pesoPalotMerma={pesoPalotMermaDefault}
                  selectedVarietyId={selectedVarietyId}
                  selectedWarehouseIds={selectedWarehouseIds}
                  variedades={variedades}
                  variedadesEntrada={[...variedadesFlujos, ...variedades]}
                />
              )}
            </Box>
          </Box>
        </AnimatedSection>
      </Stack>
    </AnimatedPage>
  );
};
